import axios from 'axios';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import IUser from '../types/user.type';

const API_URL = `${process.env.REACT_APP_API_BASE_URL}`;

interface AuthContextType {
	user: IUser | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	customLogin: (email: string, passwd: string) => Promise<void>;
	login: (credential: string) => Promise<void>;
	logout: () => Promise<void>;
	checkAuth: () => Promise<void>;
	clearAuth: () => void; // Add this for the interceptor to use
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within AuthProvider');
	}
	return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [user, setUser] = useState<IUser | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const navigate = useNavigate();

	const clearAuth = () => {
		// Clear all storage keys consistently
		localStorage.removeItem('access_token');
		localStorage.removeItem('refresh_token');
		localStorage.removeItem('user'); // For axios interceptor compatibility
		delete axios.defaults.headers.common['Authorization'];
		setUser(null);
	};

	// Add this inside the AuthProvider component, after the clearAuth function definition
	useEffect(() => {
		// Make clearAuth available globally for the interceptor
		(window as any).__authContext = { clearAuth };

		return () => {
			delete (window as any).__authContext;
		};
	}, []);

	const checkAuth = async () => {
		try {
			setIsLoading(true);

			// First check if user is authenticated via Django session
			const djangoAuthResponse = await axios.get(`${API_URL}auth/check_django_auth/`, {
				withCredentials: true,
				headers: {
					'Content-Type': 'application/json',
				},
			});


			if (djangoAuthResponse.data.authenticated) {

				// User is authenticated via Django admin, set tokens and user
				const { access, refresh, user: userData } = djangoAuthResponse.data;

				// Store tokens consistently for both systems
				localStorage.setItem('access_token', access);
				localStorage.setItem('refresh_token', refresh);
				localStorage.setItem(
					'user',
					JSON.stringify({
						access,
						refresh,
						user: userData,
					})
				); // For axios interceptor

				// Set axios default header
				axios.defaults.headers.common['Authorization'] = `Bearer ${access}`;

				setUser(userData);
				return;
			}

			// If not authenticated via Django, check for existing JWT tokens
			const accessToken = localStorage.getItem('access_token');
			if (accessToken) {
				try {
					axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
					const response = await axios.get(API_URL + 'auth/verify_token/');
					setUser(response.data);

					// Ensure user object exists for axios interceptor
					const userObj = JSON.parse(localStorage.getItem('user') || '{}');
					if (!userObj.access) {
						localStorage.setItem(
							'user',
							JSON.stringify({
								access: accessToken,
								refresh: localStorage.getItem('refresh_token'),
								user: response.data,
							})
						);
					}
				} catch (error) {
					console.log('❌ JWT token invalid, clearing auth');
					// Token is invalid, clear everything
					clearAuth();
				}
			} else {
				console.log('No tokens found, user not authenticated');
				setUser(null);
			}
		} catch (error) {
			// Only clear auth if it's not a network error
			if (error && typeof error === 'object' && 'response' in error) {
				const axiosError = error as { response: { status: number } };
				if (axiosError.response && axiosError.response.status !== 500) {
					clearAuth();
				}
			} else {
				// If it's not an axios error, clear auth
				clearAuth();
			}
		} finally {
			setIsLoading(false);
		}
	};

	const login = async (credential: string) => {
		try {
			const response = await axios.post(API_URL + 'auth/google_login/', { credential });
			const { access, refresh, user: userData } = response.data;

			// Store consistently for both systems
			localStorage.setItem('access_token', access);
			localStorage.setItem('refresh_token', refresh);
			localStorage.setItem(
				'user',
				JSON.stringify({
					access,
					refresh,
					user: userData,
				})
			);

			axios.defaults.headers.common['Authorization'] = `Bearer ${access}`;

			setUser(userData);
		} catch (error) {
			console.error('Login failed:', error);
			throw error;
		}
	};

	const customLogin = async (email: string, passwd: string) => {
		try {
			const response = await axios.post(API_URL + 'auth/basic_auth/', { email, passwd });
			const { access, refresh, user } = response.data;
			localStorage.setItem('access_token', access);
			localStorage.setItem('refresh_token', refresh);
			localStorage.setItem(
				'user',
				JSON.stringify({
					access,
					refresh,
					user,
				})
			);

			axios.defaults.headers.common['Authorization'] = `Bearer ${access}`;
			setUser(user);
		} catch (error) {
			console.log(`failed to login: ${error}`);
		}
	};

	const logout = async () => {
		try {
			const refreshToken = localStorage.getItem('refresh_token');
			await axios.post(
				API_URL + 'auth/logout/',
				{
					refresh_token: refreshToken,
				},
				{
					withCredentials: true,
				}
			);
		} catch (error) {
			console.error('Logout error:', error);
		} finally {
			// Clear tokens and user data regardless of API call success
			localStorage.removeItem('access_token');
			localStorage.removeItem('refresh_token');
			delete axios.defaults.headers.common['Authorization'];
			setUser(null);
		}
	};

	useEffect(() => {
		checkAuth();
	}, []);

	const value = {
		user,
		isAuthenticated: !!user,
		isLoading,
		customLogin,
		login,
		logout,
		checkAuth,
		clearAuth,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Make clearAuth available globally for the interceptor
(window as any).__authContext = {
	clearAuth: () => {
		// This will be set by the provider
	},
};
